import {
    ApartmentOutlined,
    CodeOutlined,
    CloseOutlined,
    DeleteOutlined,
    PlusOutlined,
    SearchOutlined,
    SnippetsOutlined,
} from "@ant-design/icons";
import { json } from "@codemirror/lang-json";
import { openSearchPanel, search } from "@codemirror/search";
import { EditorView } from "@codemirror/view";
import CodeMirror, { ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { Tooltip } from "antd";
import { useEffect, useRef, useState } from "react";
import ObjectTree from "./ObjectTree";
import { formatJson, parseInput } from "./jsonUtils";
import type { JsonValue } from "./jsonUtils";


const exampleValue = {
    project: "Ocean Planner",
    active: true,
    version: 2,
    team: {
        name: "Operations",
        members: [
            { id: 1, name: "Anna", role: "Planner" },
            { id: 2, name: "Noah", role: "Operator" },
        ],
    },
    tags: ["planning", "vessel", "aquaculture"],
};

const initialInput = formatJson(exampleValue);

interface JsonTextRange {
    from: number;
    to: number;
}

const findJsonValueRange = (source: string, path: Array<string | number>): JsonTextRange | null => {
    const skipWhitespace = (position: number) => {
        while (/\s/.test(source[position] || "")) position += 1;
        return position;
    };
    const scanString = (position: number) => {
        position += 1;
        while (position < source.length) {
            if (source[position] === "\\") position += 2;
            else if (source[position++] === '"') break;
        }
        return position;
    };
    const skipValue = (startPosition: number): number => {
        let position = skipWhitespace(startPosition);
        if (source[position] === '"') return scanString(position);
        if (source[position] === "{" || source[position] === "[") {
            const opening = source[position];
            const closing = opening === "{" ? "}" : "]";
            let depth = 0;
            while (position < source.length) {
                if (source[position] === '"') position = scanString(position);
                else {
                    if (source[position] === opening) depth += 1;
                    if (source[position] === closing && --depth === 0) return position + 1;
                    position += 1;
                }
            }
            return position;
        }
        while (position < source.length && !/[\s,}\]]/.test(source[position])) position += 1;
        return position;
    };
    let result: JsonTextRange | null = null;
    const visit = (startPosition: number, pathIndex: number): number => {
        let position = skipWhitespace(startPosition);
        if (pathIndex === path.length) {
            const to = skipValue(position);
            result = { from: position, to };
            return to;
        }
        if (source[position] === "{" && typeof path[pathIndex] === "string") {
            position = skipWhitespace(position + 1);
            while (position < source.length && source[position] !== "}") {
                const keyStart = position;
                const keyEnd = scanString(keyStart);
                const key = JSON.parse(source.slice(keyStart, keyEnd));
                position = skipWhitespace(keyEnd);
                if (source[position] === ":") position = skipWhitespace(position + 1);
                position = key === path[pathIndex] ? visit(position, pathIndex + 1) : skipValue(position);
                if (result) return position;
                position = skipWhitespace(position);
                if (source[position] === ",") position = skipWhitespace(position + 1);
            }
        } else if (source[position] === "[" && typeof path[pathIndex] === "number") {
            position = skipWhitespace(position + 1);
            let itemIndex = 0;
            while (position < source.length && source[position] !== "]") {
                position = itemIndex === path[pathIndex] ? visit(position, pathIndex + 1) : skipValue(position);
                if (result) return position;
                itemIndex += 1;
                position = skipWhitespace(position);
                if (source[position] === ",") position = skipWhitespace(position + 1);
            }
        }
        return skipValue(startPosition);
    };

    try {
        visit(0, 0);
        return result;
    } catch {
        return null;
    }
};

const INPUT_PARSE_DELAY = 300;

const codeMirrorTheme = EditorView.theme({
    "&": {
        height: "100%",
        backgroundColor: "#24272e",
        color: "#d6deeb",
        fontSize: "15px",
        position: "relative",
    },
    ".cm-content": {
        caretColor: "#c4b5fd",
        padding: "20px 0",
    },
    ".cm-line": {
        padding: "0 20px",
    },
    ".cm-gutters": {
        backgroundColor: "#1f2228",
        borderRight: "1px solid #21262d",
        color: "#6e7681",
        paddingLeft: "4px",
    },
    ".cm-activeLine, .cm-activeLineGutter": {
        backgroundColor: "rgba(110, 118, 129, 0.12)",
    },
    ".cm-selectionBackground, &.cm-focused .cm-selectionBackground": {
        backgroundColor: "rgba(88, 166, 255, 0.28) !important",
    },
    ".cm-scroller": {
        lineHeight: "24px",
        overflow: "auto",
        overscrollBehavior: "auto",
    },
    ".cm-panels-top": {
        border: "none",
        backgroundColor: "#24272e",
        padding: "10px",
    },
    ".cm-panel.cm-search": {
        display: "grid",
        gridTemplateColumns: "minmax(180px, 1fr) repeat(3, auto)",
        gap: "8px",
        boxSizing: "border-box",
        width: "100%",
        padding: "12px 48px 12px 12px",
        border: "1px solid #30363d",
        margin: "0",
        borderRadius: "0",
        backgroundColor: "#2b2f37",
        boxShadow: "0 12px 32px rgba(0, 0, 0, 0.4)",
        color: "#c9d1d9",
    },
    ".cm-panel.cm-search br": {
        display: "none",
    },
    ".cm-textfield": {
        boxSizing: "border-box",
        height: "32px",
        minWidth: "0",
        margin: "0 !important",
        padding: "0 10px",
        border: "1px solid #30363d !important",
        borderRadius: "8px !important",
        backgroundColor: "#24272e !important",
        color: "#e6edf3",
        fontSize: "12px",
        fontStyle: "normal",
        lineHeight: "30px",
        outline: "none",
        transition: "border-color 150ms, box-shadow 150ms",
    },
    ".cm-textfield::placeholder": {
        color: "#6e7681 !important",
        fontStyle: "normal",
        opacity: "1",
    },
    ".cm-textfield:focus": {
        borderColor: "#58a6ff !important",
        boxShadow: "0 0 0 3px rgba(56, 139, 253, 0.18)",
    },
    ".cm-panel.cm-search button.cm-button": {
        height: "32px",
        margin: "0",
        padding: "0 11px",
        border: "1px solid #30363d",
        borderRadius: "5px",
        background: "#30343d",
        color: "#c9d1d9",
        fontSize: "12px",
        fontWeight: "600",
        cursor: "pointer",
        transition: "all .3s",
    },
    ".cm-panel.cm-search button.cm-button:hover": {
        borderColor: "#58a6ff",
        backgroundColor: "#3a3f49",
        color: "#f0f6fc",
    },
    ".cm-panel.cm-search label": {
        display: "flex",
        gridRow: "3",
        alignItems: "center",
        gap: "4px",
        margin: "0",
        color: "#8b949e",
        fontSize: "11px",
        whiteSpace: "nowrap",
    },
    ".cm-panel.cm-search input[name=replace]": {
        gridColumn: "1",
        gridRow: "2",
    },
    ".cm-panel.cm-search button[name=replace]": {
        gridColumn: "2",
        gridRow: "2",
    },
    ".cm-panel.cm-search button[name=replaceAll]": {
        gridColumn: "3 / 5",
        gridRow: "2",
    },
    ".cm-panel.cm-search input[type=checkbox]": {
        margin: "0",
        accentColor: "#6366f1",
        transition: "all .3s",
    },
    ".cm-panel.cm-search [name=close]": {
        top: "12px",
        right: "10px",
        display: "flex",
        width: "32px",
        height: "32px",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "6px",
        color: "#8b949e",
        cursor: "pointer",
        transition: "all .3s",
        fontSize: "20px",
    },
    ".cm-panel.cm-search [name=close]:hover": {
        backgroundColor: "rgba(248, 81, 73, 0.16)",
        color: "#ff7b72",
    },
    "@media (max-width: 639px)": {
        ".cm-panel.cm-search": {
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            padding: "12px",
        },
        ".cm-panel.cm-search input[name=search]": {
            gridColumn: "1 / -1",
            gridRow: "1",
            width: "calc(100% - 40px)",
        },
        ".cm-panel.cm-search button.cm-button": {
            minWidth: "0",
            padding: "0 4px",
        },
        ".cm-panel.cm-search label": {
            flexWrap: "wrap",
            whiteSpace: "normal",
        },
        ".cm-panel.cm-search input[name=replace]": {
            gridColumn: "1 / -1",
            gridRow: "4",
            width: "100%",
        },
        ".cm-panel.cm-search button[name=replace]": {
            gridColumn: "1",
            gridRow: "5",
        },
        ".cm-panel.cm-search button[name=replaceAll]": {
            gridColumn: "2 / -1",
            gridRow: "5",
        },
    },
    ".cm-searchMatch": {
        borderRadius: "3px",
        backgroundColor: "rgba(250, 204, 21, 0.45) !important",
    },
    ".cm-searchMatch-selected": {
        backgroundColor: "rgba(251, 146, 60, 0.7) !important",
    },
});

const autoFormatPaste = EditorView.domEventHandlers({
    paste: (event, view) => {
        const pastedValue = event.clipboardData?.getData("text").trim();

        if (!pastedValue) return false;

        try {
            const convertedValue = parseInput(pastedValue);

            if (convertedValue === null || typeof convertedValue !== "object") return false;

            const selection = view.state.selection.main;
            const formattedValue = formatJson(convertedValue);

            event.preventDefault();
            view.dispatch({
                changes: {
                    from: selection.from,
                    to: selection.to,
                    insert: formattedValue,
                },
                selection: { anchor: selection.from + formattedValue.length },
            });
            return true;
        } catch {
            return false;
        }
    },
});

const codeMirrorExtensions = [json(), search({ top: true }), codeMirrorTheme, autoFormatPaste];

export const useParseObject = () => {
    const [mobilePanel, setMobilePanel] = useState<"input" | "tree">("input");
    const [input, setInput] = useState(initialInput);
    const [editorValue, setEditorValue] = useState(initialInput);
    const [parseRevision, setParseRevision] = useState(0);
    const [parsedValue, setParsedValue] = useState<JsonValue>(exampleValue);
    const [error, setError] = useState("");
    const [treeVersion, setTreeVersion] = useState(0);
    const [isInputPending, setIsInputPending] = useState(false);
    const [tabs, setTabs] = useState([{ id: 1, name: "Object 1" }]);
    const [activeTabId, setActiveTabId] = useState(1);
    const [editingTabId, setEditingTabId] = useState<number | null>(null);
    const [editingTabName, setEditingTabName] = useState("");
    const codeMirrorRef = useRef<ReactCodeMirrorRef>(null);
    const parseTimerRef = useRef<number>();
    const nextTabIdRef = useRef(2);
    const tabContentsRef = useRef(new Map<number, string>([[1, initialInput]]));

    useEffect(() => {
        document.title = "Parse Object";
    }, []);

    useEffect(() => {
        if (!input.trim()) {
            setParsedValue(null);
            setError("");
            setIsInputPending(false);
            return;
        }

        try {
            setParsedValue(parseInput(input));
            setError("");
            setTreeVersion(version => version + 1);
        } catch (parseError) {
            setError(parseError instanceof Error ? parseError.message : "Invalid JSON");
        } finally {
            setIsInputPending(false);
        }
    }, [input, parseRevision]);

    useEffect(() => () => {
        if (parseTimerRef.current) window.clearTimeout(parseTimerRef.current);
    }, []);

    const scheduleParse = (value: string, delay = INPUT_PARSE_DELAY) => {
        if (parseTimerRef.current) window.clearTimeout(parseTimerRef.current);

        tabContentsRef.current.set(activeTabId, value);
        setIsInputPending(true);
        parseTimerRef.current = window.setTimeout(() => {
            setInput(value);
            setParseRevision(revision => revision + 1);
        }, delay);
    };

    const isParsing = isInputPending;

    const clearInput = () => {
        setEditorValue("");
        scheduleParse("", 0);
    };

    const pasteInput = async () => {
        try {
            const clipboardValue = await navigator.clipboard.readText();

            if (!clipboardValue) return;

            let nextValue = clipboardValue;

            try {
                const convertedValue = parseInput(clipboardValue.trim());

                if (convertedValue !== null && typeof convertedValue === "object") {
                    nextValue = formatJson(convertedValue);
                }
            } catch {
                // Keep the original clipboard text so the editor can show its syntax error.
            }

            setEditorValue(nextValue);
            scheduleParse(nextValue, 0);
            codeMirrorRef.current?.view?.focus();
        } catch {
            setError("Unable to read clipboard. Please allow clipboard access and try again.");
        }
    };

    const openTab = (tabId: number) => {
        if (tabId === activeTabId) return;
        if (parseTimerRef.current) window.clearTimeout(parseTimerRef.current);

        const nextInput = tabContentsRef.current.get(tabId) || "";

        setActiveTabId(tabId);
        setIsInputPending(true);
        setInput(nextInput);
        setParseRevision(revision => revision + 1);
        setTreeVersion(version => version + 1);
        setEditorValue(nextInput);
    };

    const addTab = () => {
        const tabId = nextTabIdRef.current;

        nextTabIdRef.current += 1;
        tabContentsRef.current.set(tabId, "");
        setTabs(currentTabs => [...currentTabs, { id: tabId, name: `Object ${tabId}` }]);

        if (parseTimerRef.current) window.clearTimeout(parseTimerRef.current);
        setActiveTabId(tabId);
        setInput("");
        setParseRevision(revision => revision + 1);
        setTreeVersion(version => version + 1);
        setEditorValue("");
    };

    const closeTab = (tabId: number) => {
        if (tabs.length === 1) return;
        if (parseTimerRef.current) window.clearTimeout(parseTimerRef.current);

        const tabIndex = tabs.findIndex(tab => tab.id === tabId);
        const remainingTabs = tabs.filter(tab => tab.id !== tabId);

        tabContentsRef.current.delete(tabId);
        setTabs(remainingTabs);

        if (tabId === activeTabId) {
            const nextTab = remainingTabs[Math.min(tabIndex, remainingTabs.length - 1)];
            const nextInput = tabContentsRef.current.get(nextTab.id) || "";

            setActiveTabId(nextTab.id);
            setInput(nextInput);
            setParseRevision(revision => revision + 1);
            setTreeVersion(version => version + 1);
            setEditorValue(nextInput);
        }
    };

    const startRenamingTab = (tabId: number, name: string) => {
        setEditingTabId(tabId);
        setEditingTabName(name);
    };

    const saveTabName = () => {
        if (editingTabId === null) return;

        const nextName = editingTabName.trim();

        if (nextName) {
            setTabs(currentTabs => currentTabs.map(tab =>
                tab.id === editingTabId ? { ...tab, name: nextName } : tab
            ));
        }

        setEditingTabId(null);
    };

    const navigateToInputValue = (path: Array<string | number>) => {
        const view = codeMirrorRef.current?.view;
        if (!view) return;

        const range = findJsonValueRange(view.state.doc.toString(), path);
        if (!range) return;

        view.dispatch({
            selection: { anchor: range.from, head: range.to },
            scrollIntoView: true,
        });
        view.focus();
    };

    const objectTreePanel = (
        <ObjectTree
            activePageTab="parse-object"
            error={error}
            hasApiResponse={false}
            hasSelectedApiEndpoint={false}
            input={input}
            isCallingApi={false}
            isParsing={isParsing}
            mobilePanel={mobilePanel}
            onNavigateToValue={navigateToInputValue}
            parsedValue={parsedValue}
            treeVersion={treeVersion}
        />
    );

    const render = (activePageTab: string) => (
        <>
            <div className={activePageTab === "parse-object" ? "flex min-h-0 flex-1 flex-col min-[1601px]:flex-row min-[1601px]:gap-4 min-[1601px]:overflow-hidden" : "hidden"}>
                <div className="mb-3 flex w-full shrink-0 flex-wrap gap-1 rounded-lg border border-[#373b44] bg-[#202329] p-1 min-[1601px]:mb-0 min-[1601px]:w-44 min-[1601px]:flex-col min-[1601px]:flex-nowrap min-[1601px]:overflow-y-auto">
                    {tabs.map(tab => (
                        <div
                            className={`group flex h-8 shrink-0 items-center rounded-md px-3 text-sm font-semibold transition min-[1601px]:w-full ${tab.id === activeTabId ? "bg-slate-800 text-indigo-300 shadow-sm ring-1 ring-slate-700" : "text-slate-500 hover:bg-slate-800/70 hover:text-slate-200"}`}
                            key={tab.id}
                        >
                            {editingTabId === tab.id ? (
                                <input
                                    aria-label="Tab name"
                                    autoFocus
                                    className="h-7 min-w-0 flex-1 rounded border border-indigo-500 bg-slate-950 px-2 text-sm font-semibold text-slate-200 outline-none"
                                    maxLength={40}
                                    onBlur={saveTabName}
                                    onChange={event => setEditingTabName(event.target.value)}
                                    onKeyDown={event => {
                                        if (event.key === "Enter") saveTabName();
                                        if (event.key === "Escape") setEditingTabId(null);
                                    }}
                                    onFocus={event => event.currentTarget.select()}
                                    value={editingTabName}
                                />
                            ) : (
                                <Tooltip mouseEnterDelay={0.35} title={tab.name}>
                                    <button
                                        className="h-full min-w-0 flex-1 truncate text-left"
                                        onClick={() => openTab(tab.id)}
                                        onDoubleClick={() => startRenamingTab(tab.id, tab.name)}
                                        type="button"
                                    >
                                        {tab.name}
                                    </button>
                                </Tooltip>
                            )}
                            {tabs.length > 1 && (
                                <button
                                    aria-label={`Close ${tab.name}`}
                                    className="-mr-1 ml-2 flex h-5 w-5 items-center justify-center rounded text-[10px] text-slate-500 transition-colors duration-150 hover:bg-rose-500/15 hover:text-rose-400"
                                    onClick={() => closeTab(tab.id)}
                                    type="button"
                                >
                                    <CloseOutlined />
                                </button>
                            )}
                        </div>
                    ))}
                    <button
                        aria-label="Add parser tab"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-800 hover:text-indigo-400 hover:shadow-sm min-[1601px]:w-full"
                        onClick={addTab}
                        title="Add tab"
                        type="button"
                    >
                        <PlusOutlined />
                    </button>
                </div>

                <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-2 lg:overflow-hidden">
                    <section className={`${mobilePanel === "input" ? "flex" : "hidden sm:flex"} min-h-0 sm:min-h-[420px] flex-col overflow-hidden rounded-lg border border-[#373b44] bg-[#24272e] shadow-xl shadow-black/20`}>
                        <div className="flex h-[42px] shrink-0 items-center justify-between gap-1.5 border-b border-[#373b44] bg-[#202329] px-2.5 py-[5px] sm:h-[50px] sm:px-5 sm:py-0">
                            <div className="flex items-center gap-3">
                                <h2 className="m-0 text-base font-bold text-slate-100 pb-[2px]">Input</h2>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className={`flex items-center gap-1.5 text-xs font-semibold ${error ? "text-rose-600" : isParsing ? "text-amber-600" : "text-emerald-600"}`}>
                                    <span className={`h-1.5 w-1.5 rounded-full ${error ? "bg-rose-500" : isParsing ? "animate-pulse bg-amber-500" : "bg-emerald-500"}`} />
                                    {isParsing ? "Parsing..." : error ? "Invalid JSON" : "Valid JSON"}
                                </div>
                                <button
                                    aria-label="Search input"
                                    className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-indigo-500/10 hover:text-indigo-400"
                                    onClick={() => {
                                        if (codeMirrorRef.current?.view) openSearchPanel(codeMirrorRef.current.view);
                                    }}
                                    title="Search input (Ctrl/Cmd + F)"
                                    type="button"
                                >
                                    <SearchOutlined />
                                </button>
                                <button
                                    aria-label="Paste from clipboard"
                                    className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-indigo-500/10 hover:text-indigo-400"
                                    onClick={pasteInput}
                                    title="Paste from clipboard"
                                    type="button"
                                >
                                    <SnippetsOutlined />
                                </button>
                                <button
                                    aria-label="Clear input"
                                    className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-rose-500/10 hover:text-rose-400"
                                    onClick={clearInput}
                                    title="Clear input"
                                    type="button"
                                >
                                    <DeleteOutlined />
                                </button>
                            </div>
                        </div>
                        <div className="min-h-0 flex-1 overflow-hidden bg-[#24272e] sm:min-h-[320px] lg:min-h-0">
                            <CodeMirror
                                aria-label="JSON input"
                                basicSetup={{
                                    bracketMatching: true,
                                    closeBrackets: true,
                                    foldGutter: true,
                                    highlightActiveLine: true,
                                    highlightActiveLineGutter: true,
                                    lineNumbers: true,
                                }}
                                className="h-full"
                                extensions={codeMirrorExtensions}
                                height="100%"
                                indentWithTab
                                onChange={value => {
                                    setEditorValue(value);
                                    scheduleParse(value);
                                }}
                                placeholder='Paste an object, for example: { "name": "Ocean Planner" }'
                                ref={codeMirrorRef}
                                theme="dark"
                                value={editorValue}
                            />
                        </div>
                        {error && (
                            <div className="min-h-10 border-t border-rose-900/50 bg-rose-950/30 px-5 py-2 font-mono text-xs text-rose-400">
                                {error}
                            </div>
                        )}
                    </section>

                    {activePageTab === "parse-object" && objectTreePanel}
                </div>
            </div>

            {activePageTab === "parse-object" && (
                <nav aria-label="Parse object panels" className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-2 border-t border-[#373b44] bg-[#202329] pb-[env(safe-area-inset-bottom)] sm:hidden">
                    {([{ key: "input", label: "Input", icon: <CodeOutlined /> }, { key: "tree", label: "Object tree", icon: <ApartmentOutlined /> }] as const).map(panel => (
                        <button aria-pressed={mobilePanel === panel.key} className={`relative flex h-12 flex-row items-center justify-center gap-2 whitespace-nowrap text-xs font-semibold transition-colors [&_.anticon]:text-base ${mobilePanel === panel.key ? "text-indigo-300" : "text-slate-400 hover:text-slate-200"}`} key={panel.key} onClick={() => setMobilePanel(panel.key)} type="button">
                            {panel.icon}{panel.label}
                        </button>
                    ))}
                </nav>
            )}
        </>
    );
    return { render };
};
